import { Product } from '../value-objects';
import { Entity } from './entity';
import { ProtocolAlgorithm } from './protocol-algorithm';

export interface AlgorithmItem {
  id: string;
  name: string;
  version: string;
  regulated: boolean;
  deprecated: boolean;
  environment: ExecutionEnvironment;
  application: Product;
  createdAt: Date;
}

export enum ExecutionEnvironment {
  CLOUD = 'cloud',
  MOBILE = 'mobile',
}

export enum Engine {
  MAP = 'map',
  TFLITE = 'TFLite',
  MLKIT = 'MLKit',
  OPENCV = 'OpenCV',
}

export interface AlgorithmAttributes extends Entity {
  name: string;
  version: string;
  description?: string;
  regulated: boolean;
  deprecated: boolean;
  config: any;
  environment: ExecutionEnvironment;
  application: Product;
  numOrganizations: number;
  numWorkspaces: number;
}

export enum AlgorithmException {
  INVALID_NAME = 'name is required',
  INVALID_VERSION = 'version is required',
  INVALID_CONFIG = 'config not provided',
  INVALID_ENVIRONMENT = 'execution environment not provided',
}

export interface Assignment {
  methodTypeId: string;
  analysisTypeId?: string;
}

export class Algorithm implements Entity {
  constructor(private attributes: AlgorithmAttributes) {
    if (!attributes.name) throw new Error(AlgorithmException.INVALID_NAME);
    if (!attributes.version) throw new Error(AlgorithmException.INVALID_VERSION);
    if (!attributes.config) throw new Error(AlgorithmException.INVALID_CONFIG);
    if (!attributes.environment) throw new Error(AlgorithmException.INVALID_ENVIRONMENT);
  }

  public get numOrganizations(): number {
    return this.attributes.numOrganizations;
  }

  public get numWorkspaces(): number {
    return this.attributes.numWorkspaces;
  }

  public get id(): string | undefined {
    return this.attributes.id;
  }

  public get name(): string {
    return this.attributes.name;
  }

  public get version(): string {
    return this.attributes.version;
  }

  public get description(): string | undefined {
    return this.attributes.description;
  }

  public get regulated(): boolean {
    return this.attributes.regulated;
  }

  public get config(): any {
    return this.attributes.config;
  }

  private get cloudOutputLabels(): Set<string> {
    const labels = this.config?.postproc_opts?.labels;
    if (!labels || Object.keys(labels).length === 0) return new Set();
    const values = Object.values(labels).flatMap((value) => (Array.isArray(value) ? value : [value]));
    return new Set(values.filter((value): value is string => typeof value === 'string'));
  }

  private findValueByKey(obj: unknown, targetKey: string): string[] {
    if (!obj || typeof obj !== 'object') return [];

    // Check if the current object has the target key
    if (targetKey in obj) {
      const value = (obj as Record<string, unknown>)[targetKey];
      // Ensure the value is always returned as an array and filter for strings
      const values = Array.isArray(value) ? value : [value];
      return values.filter((v): v is string => typeof v === 'string');
    }

    // Recursively search in nested objects and arrays
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = (obj as Record<string, unknown>)[key];
        if (typeof value === 'object' && value !== null) {
          const result = this.findValueByKey(value, targetKey);
          if (result.length > 0) {
            return result;
          }
        }
      }
    }

    return [];
  }

  private get scrapTFLiteOutputLabels(): Set<string> {
    const outputSignature = this.config[0]?.inference?.outputSignature;
    if (!outputSignature) return new Set();

    const uuids = this.findValueByKey(outputSignature, 'uuid');
    return new Set(uuids);
  }

  private get scrapOpenCVOutputLabels(): Set<string> {
    const uuid = this.config[0]?.inference?.uuid;
    if (!uuid) return new Set();

    // Ensure uuid is always treated as an array and filter for strings
    const uuids = Array.isArray(uuid) ? uuid : [uuid];
    return new Set(uuids.filter((v): v is string => typeof v === 'string'));
  }

  public get engine(): Engine | undefined {
    const engineValue = this.config[0]?.executor?.engine;
    const validEngines = Object.values(Engine) as string[];
    return validEngines.includes(engineValue) ? (engineValue as Engine) : undefined;
  }

  private get mobileOutputLabels(): Set<string> {
    switch (this.engine) {
      case Engine.OPENCV:
        return this.scrapOpenCVOutputLabels;
      case Engine.TFLITE:
        return this.scrapTFLiteOutputLabels;
      default: // includes MLKit and map engines
        return new Set();
    }
  }

  public get outputLabels(): Set<string> {
    return this.environment === ExecutionEnvironment.CLOUD ? this.cloudOutputLabels : this.mobileOutputLabels;
  }

  public get environment(): ExecutionEnvironment {
    return this.attributes.environment;
  }

  public get application(): Product {
    return this.attributes.application ?? Product.ADAPTASPOT; // REVIEW default value
  }

  public get deprecated(): boolean {
    return this.attributes.deprecated;
  }

  public get createdAt(): Date | undefined {
    return this.attributes.createdAt;
  }

  public get updatedAt(): Date | undefined {
    return this.attributes.updatedAt;
  }

  public executableOn(env: ExecutionEnvironment): boolean {
    return this.environment === env;
  }

  public updateConfig(config: any | any[]): Algorithm {
    this.attributes.config = config;
    return this;
  }

  public assign(params: Assignment): ProtocolAlgorithm {
    return new ProtocolAlgorithm({ ...params, model: this });
  }

  public updateDescription(value: string): Algorithm {
    this.attributes.description = value;
    return this;
  }

  public updateName(value: string): Algorithm {
    this.attributes.name = value;
    return this;
  }

  public updateVersion(value: string): Algorithm {
    this.attributes.version = value;
    return this;
  }

  public changeRegulatedStatus(status: boolean): Algorithm {
    this.attributes.regulated = status;
    return this;
  }

  public changeDeprecationStatus(status: boolean): Algorithm {
    this.attributes.deprecated = status;
    return this;
  }

  public is(product: Product): boolean {
    return this.application === product;
  }
}
